import {useRouter} from 'next/router';
import {useCallback} from 'react';
import {twMerge} from 'tailwind-merge';
import {requestContext} from '../../common/requestContext';
import useTypedDispatch from '../../common/useTypedDispatch';
import useTypedSelector from '../../common/useTypedSelector';
import {useDialog} from '../../dialog/useDialog.hook';
import {LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED} from '../../domain/content/[seriesId]/viewer/[productId]/constant/viewerConstant';
import {ViewerSettingContainer} from '../../domain/content/[seriesId]/viewer/[productId]/container/ViewerSettingContainer';
import {
  setCurrentViewerGuide,
  setIsViewerToolTipExposed,
} from '../../domain/content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import {setIsViewerMenuExposed} from '../../domain/content/[seriesId]/viewer/[productId]/state/viewerMenuSlice';
import {showViewerGuide} from '../../domain/content/[seriesId]/viewer/[productId]/util/showViewerGuide';
import {AgeGrade, SlideType} from '../../graphql/generated/types';
import {putTiaraObjectProps} from '../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../reactQuery/constant';
import {useSinglePreviewQuery} from '../../wrapper';
import {useViewerInfoQuery} from '../../wrapper/viewerWrapper';
import {MobileViewerHeader} from '../organisms/MobileViewerHeader/MobileViewerHeader';
import {MobileLayoutViewerTitleHeader} from './MobileLayoutViewerTitleHeader';

export function MobileLayoutViewerHeader() {
  const router = useRouter();
  const dispatch = useTypedDispatch();

  const {productId, seriesId} = router.query;
  const currentProductId = Number(productId);
  const currentSeriesId = Number(seriesId);

  const {data, error, isFetching} = useViewerInfoQuery(
    {
      seriesId: currentSeriesId,
      productId: currentProductId,
    },
    ONLY_DATA_CONFIG
  );
  const viewerData = data;

  const {isNonstopWatching, isToolTipExposed, hideAgeGradeLabel} =
    useTypedSelector(state => ({
      isNonstopWatching:
        !!state.viewerInfo.nonstopWatchingInSeries[currentSeriesId]
          ?.isNonstopWatching,
      isToolTipExposed: state.viewerInfo.isToolTipExposed,
      hideAgeGradeLabel: state.viewerInfo.hideAgeGradeLabel,
    }));
  const {prevViewerGuide, currentViewerGuide} = useTypedSelector(state => ({
    prevViewerGuide: state.viewerInfo.prevViewerGuide,
    currentViewerGuide: state.viewerInfo.currentViewerGuide,
  }));
  const {isViewerMenuExposed} = useTypedSelector(state => ({
    isViewerMenuExposed: state.viewerMenu.isExposed,
  }));

  const {isMobile, isWebView} = requestContext.requestInfo.display;

  const showViewerGuideWhenDifferent = useCallback(() => {
    if (currentViewerGuide && prevViewerGuide !== currentViewerGuide) {
      showViewerGuide({viewerGuideType: currentViewerGuide});
    }
    dispatch(setCurrentViewerGuide(currentViewerGuide));
  }, [currentViewerGuide, prevViewerGuide, dispatch]);

  const {DialogWrapper, Dialog, showDialog} = useDialog({
    isMobile,
    onClose: showViewerGuideWhenDifferent,
  });

  const onClickContentsHomeButton = useCallback(() => {
    router.push(`/content/${currentSeriesId}`);
  }, [router, currentSeriesId]);

  const handleClickMobileSettingButton = useCallback(() => {
    //모바일의 경우 뷰어 설정이 켜졌을 때 뷰어 메뉴가 꺼지게
    dispatch(setIsViewerMenuExposed({isExposed: false}));
    showDialog();
    //뷰어 설정이 열리고 툴팁이 열려 있다면 툴팁을 닫습니다
    if (isToolTipExposed) {
      dispatch(setIsViewerToolTipExposed(false));
      window.localStorage.setItem(
        LOCAL_STORAGE_VIEWER_TOOLTIP_IS_CLOSED,
        'true'
      );
    }
  }, [dispatch, isToolTipExposed, showDialog]);

  const singlePreviewResult = useSinglePreviewQuery({
    productId: currentProductId,
  });
  const {data: singlePreviewData} = singlePreviewResult;

  const productTitle = singlePreviewData?.singlePreview?.title;

  if (
    error ||
    isFetching ||
    !viewerData ||
    !currentSeriesId ||
    !currentProductId
  ) {
    return (
      <MobileLayoutViewerTitleHeader
        title={productTitle}
        onClickContentHome={onClickContentsHomeButton}
      />
    );
  }

  const showAgeGradeLabel =
    !hideAgeGradeLabel &&
    [SlideType.Comic, SlideType.Epub].includes(viewerData.item.slideType) &&
    viewerData.seriesItem.ageGrade === AgeGrade.Fifteen;

  return (
    <>
      <div
        className={twMerge(
          'w-full',
          (isWebView || !isViewerMenuExposed) && 'hidden'
        )}
        {...putTiaraObjectProps({
          click: {
            layer1: '뷰어메뉴',
          },
          customProps: {
            viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
            is_shortdrama: viewerData.item.isShortsDrama ? 'y' : undefined,
          },
        })}
      >
        <MobileViewerHeader
          title={viewerData.item.title}
          isTextViewer={viewerData.item.slideType === SlideType.Epub}
          hasSetting={viewerData.item.slideType !== SlideType.TalkHumor}
          showAgeGradeLabel={showAgeGradeLabel}
          onClickContentsHomeButton={onClickContentsHomeButton}
          onClickSettingButton={handleClickMobileSettingButton}
        />
      </div>
      <DialogWrapper
        isShadow={true}
        isFoldableCenterMode={false}
        isUsingScrollBlock={false}
        className="z-150"
        closeWhenSwipe
        {...putTiaraObjectProps({
          click: {
            layer1: '뷰어설정',
          },
          customProps: {
            viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
          },
        })}
      >
        <Dialog.Dimmed opacity={0} />
        <Dialog.ViewerSetting>
          <ViewerSettingContainer
            seriesId={currentSeriesId}
            productId={currentProductId}
            slideType={viewerData.item.slideType}
          />
        </Dialog.ViewerSetting>
      </DialogWrapper>
    </>
  );
}
